import { useEffect, useState } from "react";

import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import { SearchQuery, createTextSearchQuery } from "../../../services/api/search";
import Switch from "../../../components/switch/Switch";
import { isOdooConnected, odooSyncQueries } from "../../../services/odoo/odoo";

/**
 * getQueriesFromFilter: traduit les filtres pièces en requêtes de recherche.
 *
 * Vit ici, à côté du formulaire qui produit ces filtres, comme dans les autres vues de liste : la
 * table et l'export lisent ainsi la même définition et ne peuvent pas diverger. C'était
 * auparavant du code en ligne dans Parts.tsx, donc l'export n'avait aucun moyen de le réutiliser.
 *
 * @param filters - valeurs actives du drawer de filtres
 */
export const getQueriesFromFilter = (filters?: Partial<EasyTech.PartsFilterType>): SearchQuery[] => {
  const queries: SearchQuery[] = [];
  if (!filters) return queries;

  // Libellé et référence identiques : une seule requête sur les deux champs, en OU. Deux requêtes
  // séparées seraient combinées en ET et ne renverraient que les pièces dont le libellé *et* la
  // référence valent la même chose — presque toujours aucune.
  if (filters.label && filters.ref && filters.label === filters.ref) {
    queries.push(createTextSearchQuery(["label", "ref"], filters.label));
  } else {
    if (filters.label) queries.push(createTextSearchQuery(["label"], filters.label));
    if (filters.ref) queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  // Les deux filtres Odoo, identiques à ceux de la liste clients — voir odooSyncQueries.
  queries.push(...odooSyncQueries(filters));

  return queries;
};

interface PartsFilterFormProps {
  data?: Partial<EasyTech.PartsFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

const PartsFilterForm = (props: PartsFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.PartsFilterType>>(
    props.data || {}
  );

  /** Les filtres Odoo n'ont de sens que là où Odoo est branché — comme la colonne de la table. */
  const odooConnected = isOdooConnected();

  useEffect(() => {
    setFormData(props.data || {});
  }, [props.data]);

  // const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
  //     const { name, value } = event.target;
  //     let tmp = structuredClone(formData)
  //     tmp[name as keyof EasyTech.WorkordersFilterType] = value
  //     setFormData(tmp);
  // };

  const handleSelectChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    tmp[name as keyof Partial<EasyTech.PartsFilterType>] = value;
    props.onChange(name, value);
    setFormData(tmp);
  };

  /**
   * Activer « non synchronisées » vide l'identifiant saisi plutôt que de le garder inerte : sinon
   * une puce de filtre continuerait d'annoncer un identifiant qui ne participe plus à la recherche.
   *
   * @param checked - nouvel état de l'interrupteur
   */
  const handleNotSyncedChange = (checked: boolean) => {
    if (checked) handleSelectChange("", "externalId");
    handleSelectChange(checked, "odooNotSynced");
  };

  const submit = (e: any) => {
    e.preventDefault();
    props.onSubmit();
  };


  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-parts-filter"}>
      <div className="grid grid-cols-1 gap-4">
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:form:fields:label:label")}</label>
          <Input
            value={formData.label || ""}
            onChange={(e) => handleSelectChange(e.target.value, "label")}
            className="w-full"
            size="md"
            placeholder={t("common:form:fields:label:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:form:fields:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleSelectChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("common:ref:placeholder")}
          />
        </div>

        {odooConnected && (
          <>
            <div className="field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("piece:odoo:externalId:label")}</label>
              <Input
                value={formData.externalId || ""}
                onChange={(e) => handleSelectChange(e.target.value, "externalId")}
                disabled={!!formData.odooNotSynced}
                className="w-full"
                size="md"
                placeholder={t("piece:odoo:externalId:placeholder")}
              />
            </div>

            {/* Libellé au-dessus de l'interrupteur, comme les deux interrupteurs du filtre clients. */}
            <div className="field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("piece:odoo:filters:notSynced")}</label>
              <Switch checked={!!formData.odooNotSynced}
                onChange={handleNotSyncedChange}/>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default PartsFilterForm;
